import { browserUrl, BUILD_PREFIX, isDraftPath, withBuildPrefix } from './build-paths.js';

const API_BASE = withBuildPrefix('/api');
const BACKOFFICE_API = withBuildPrefix('/api/backoffice');
let draftCsrfToken = null;

async function ensureDraftSession() {
  if (draftCsrfToken) return draftCsrfToken;
  const response = await fetch(`${BACKOFFICE_API}/auth/session`, { credentials: 'same-origin' });
  if (!response.ok) {
    window.location.assign(withBuildPrefix('/backoffice/'));
    throw new Error('Authentication required');
  }
  const session = await response.json();
  if (session.password_change_required) {
    window.location.assign(withBuildPrefix('/backoffice/'));
    throw new Error('Password change required');
  }
  draftCsrfToken = session.csrf_token;
  return draftCsrfToken;
}

export async function apiGet(path, options = {}) {
  const headers = {};

  let res = await fetch(`${API_BASE}${path}`, { credentials: 'same-origin', headers });

  if (res.status === 401 && options.auth === 'draft') window.location.assign(withBuildPrefix('/backoffice/'));

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function apiPost(path, body, options = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (options.auth === 'draft') headers['X-CSRF-Token'] = await ensureDraftSession();

  let res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });

  if (res.status === 401 && options.auth === 'draft') window.location.assign(withBuildPrefix('/backoffice/'));

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function fetchPages() {
  return apiGet('/pages');
}

export async function fetchSearchResults(query, page = 1) {
  const params = new URLSearchParams({ q: query, page: String(page) });
  return apiGet(`/search?${params}`);
}

export async function fetchSiteSettings() {
  const draftMode = isDraftPath();
  const historical = draftMode ? new URLSearchParams(window.location.search).get('settings_version') : null;
  const query = historical && /^[1-9][0-9]*$/.test(historical) ? `?settings_version=${historical}` : '';
  const settings = await apiGet(draftMode ? `/drafts/site${query}` : '/site', draftMode ? { auth: 'draft' } : {});
  applySiteMeta(settings);
  return settings;
}

export async function fetchPage(slug) {
  const draftMode = isDraftPath();
  const search = new URLSearchParams(window.location.search);
  const version = search.get('page_version');
  const pageId = search.get('page_id');
  const historical = draftMode && /^[1-9][0-9]*$/.test(version || '') && /^[1-9][0-9]*$/.test(pageId || '')
    ? `?page_version=${version}&page_id=${pageId}` : '';
  const page = await apiGet(`/${draftMode ? 'drafts' : 'pages'}/${slug}${historical}`, draftMode ? { auth: 'draft' } : {});
  updatePageMeta(page, draftMode);
  return page;
}

export function publishDraftPage(slug) {
  return apiPost(`/drafts/${encodeURIComponent(slug)}/publish`, {}, { auth: 'draft' });
}

export function publishDraftSiteSettings() {
  return apiPost('/drafts/site/publish', {}, { auth: 'draft' });
}

export function fetchDraftSitemap() { return apiGet('/drafts/sitemap', { auth: 'draft' }); }

async function backofficeRequest(path, method = 'GET', body = undefined) {
  const headers = {};
  if (method !== 'GET') headers['X-CSRF-Token'] = await ensureDraftSession();
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const response = await fetch(`${BACKOFFICE_API}${path}`, {
    method, headers, credentials: 'same-origin', body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error?.message || `HTTP ${response.status}`);
  }
  return response.json();
}

export function fetchTemplateStatus() { return backofficeRequest('/bootstrap').then(data => data.status.templates); }
export function fetchTemplateHistory() { return backofficeRequest('/templates/history').then(data => data.releases); }
export function resetTemplateDrafts() { return backofficeRequest('/templates/reset', 'POST'); }
export function restoreTemplateRelease(releaseId) { return backofficeRequest(`/templates/releases/${releaseId}/restore`, 'POST'); }
export function publishTemplateDrafts() { return backofficeRequest('/templates/publish', 'POST', { confirmed: true }); }
export function republishTemplateRelease(releaseId) { return backofficeRequest(`/templates/releases/${releaseId}/publish`, 'POST', { confirmed: true }); }

/** Top-level pages in draft state, mapped for navigation/routing in draft preview. */
export async function fetchDraftNavPages() {
  const sitemap = await fetchDraftSitemap();
  return sitemap
    .filter((item) => item.type !== 'site_settings')
    .map((p) => {
      const hasDraft = p.draft_title !== null && p.draft_title !== undefined;
      const deleted = hasDraft ? p.draft_is_deleted : p.is_deleted;
      return {
        id: p.id,
        slug: (hasDraft ? p.draft_slug : p.slug) || p.slug,
        title: (hasDraft ? p.draft_title : p.title) || p.title,
        menu_order: (hasDraft ? p.draft_menu_order : p.menu_order) ?? 0,
        is_published: 1,
        is_deleted: deleted ? 1 : 0,
      };
    })
    .filter((p) => p.slug && !String(p.slug).startsWith('__draft_'))
    .sort((a, b) => (a.menu_order - b.menu_order) || String(a.title).localeCompare(String(b.title)));
}
export function fetchPageHistory(pageId) { return apiGet(`/drafts/pages/${pageId}/history`, { auth: 'draft' }); }
export function fetchSiteHistory() { return apiGet('/drafts/site/history', { auth: 'draft' }); }
export function resetPageDraft(pageId) { return apiPost(`/drafts/pages/${pageId}/reset`, {}, { auth: 'draft' }); }
export function resetSiteDraft() { return apiPost('/drafts/site/reset', {}, { auth: 'draft' }); }
export function restorePageVersion(pageId, versionId) { return apiPost(`/drafts/pages/${pageId}/restore`, { version_id: versionId }, { auth: 'draft' }); }
export function restoreSiteVersion(versionId) { return apiPost('/drafts/site/restore', { version_id: versionId }, { auth: 'draft' }); }
export function republishPageVersion(pageId, versionId) { return apiPost(`/drafts/pages/${pageId}/versions/${versionId}/publish`, {}, { auth: 'draft' }); }
export function republishSiteVersion(versionId) { return apiPost(`/drafts/site/versions/${versionId}/publish`, {}, { auth: 'draft' }); }

export async function signIn(username, password) {
  return apiPost('/signin', { username, password });
}

function updatePageMeta(page, draftMode = false) {
  const headline = page?.content?.meta?.headline || page?.content?.headline || page?.title || __SITE_CONFIG__.name || 'Website';
  const description = page?.content?.meta?.description || page?.content?.meta?.subtitle || page?.content?.subtitle || page?.description || '';
  const suffix = storeSite()?.seo?.title_suffix || __SITE_CONFIG__.name || 'Website';
  document.title = draftMode ? `Draft: ${headline}` : `${headline} - ${suffix}`;
  setMeta('description', description);

  const lastUpdated = page?.last_updated || page?.published_at || page?.updated_at || '';
  setMeta('last-modified', lastUpdated);
  setMeta('article:modified_time', lastUpdated, 'property');

  if (draftMode) return;

  const canonical = window.location.origin + window.location.pathname;
  const image = firstImageUrl(page?.content) || absoluteUrl(storeSite()?.brand?.logo_image || '');
  setMeta('og:title', headline, 'property');
  setMeta('og:description', description, 'property');
  setMeta('og:type', 'website', 'property');
  setMeta('og:url', canonical, 'property');
  if (image) setMeta('og:image', image, 'property');
  setMeta('twitter:card', image ? 'summary_large_image' : 'summary');
  setMeta('twitter:title', headline);
  setMeta('twitter:description', description);
  setLink('canonical', canonical);
  applyJsonLd(page, headline, description, image, canonical, lastUpdated);
}

function absoluteUrl(url) {
  if (!url) return '';
  return url.startsWith('http') ? url : window.location.origin + browserUrl(url);
}

function firstImageUrl(content) {
  let found = '';
  const walk = (elements) => {
    for (const el of elements || []) {
      if (found) return;
      if (el.type === 'image' && el.props?.src) found = el.props.src;
      if (el.type === 'slideshow') {
        for (const slide of el.props?.slides || []) {
          if (!found && slide.image) found = slide.image;
        }
      }
      if ((el.type === 'grid' || el.type === 'products')) {
        for (const item of el.props?.items || []) {
          if (!found && item.image) found = item.image;
        }
      }
      walk(el.children);
    }
  };
  walk(content?.elements);
  return found ? absoluteUrl(found) : '';
}

function priceValue(text) {
  const digits = String(text || '').replace(/[^0-9]/g, '');
  const value = parseInt(digits, 10);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function applyJsonLd(page, headline, description, image, canonical, lastUpdated) {
  const settings = storeSite() || {};
  const graph = [
    {
      '@type': 'Organization',
      '@id': window.location.origin + BUILD_PREFIX + '/#org',
      name: settings.brand?.name || __SITE_CONFIG__.name || 'Website',
      url: window.location.origin + BUILD_PREFIX + '/',
      ...(settings.brand?.description ? { description: settings.brand.description } : {}),
      ...(settings.brand?.logo_image ? { logo: absoluteUrl(settings.brand.logo_image) } : {}),
    },
    {
      '@type': 'WebSite',
      name: settings.brand?.name || __SITE_CONFIG__.name || 'Website',
      url: window.location.origin + BUILD_PREFIX + '/',
      inLanguage: settings.seo?.language || 'en',
    },
    {
      '@type': 'WebPage',
      name: headline,
      ...(description ? { description } : {}),
      url: canonical,
      ...(image ? { primaryImageOfPage: image } : {}),
      ...(lastUpdated ? { dateModified: lastUpdated } : {}),
      isPartOf: { '@id': window.location.origin + BUILD_PREFIX + '/#org' },
    },
  ];

  const productItems = [];
  const collect = (elements) => {
    for (const el of elements || []) {
      if (el.type === 'products') productItems.push(...(el.props?.items || []));
      collect(el.children);
    }
  };
  collect(page?.content?.elements);

  if (productItems.length) {
    graph.push({
      '@type': 'ItemList',
      numberOfItems: productItems.length,
      itemListElement: productItems.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: item.title,
          ...(item.description ? { description: item.description } : {}),
          ...(item.image ? { image: absoluteUrl(item.image) } : {}),
          ...(priceValue(item.price) ? {
            offers: {
              '@type': 'Offer',
              price: priceValue(item.price),
              priceCurrency: 'NOK',
              availability: 'https://schema.org/InStock',
            },
          } : {}),
        },
      })),
    });
  }

  let script = document.head.querySelector('script[data-site-jsonld]');
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-site-jsonld', '');
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
}

export function applySiteMeta(settings) {
  if (!settings) return;
  document.documentElement.lang = settings.seo?.language || 'en';
  document.title = settings.seo?.default_title || document.title;
  setMeta('description', settings.seo?.default_description || '');
  if (settings.seo?.favicon_url) {
    let link = document.head.querySelector('link[rel="icon"]');
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = browserUrl(settings.seo.favicon_url);
  }
  window.__siteSettings = settings;
}

function storeSite() { return window.__siteSettings || null; }

function setMeta(name, content, attr = 'name') {
  if (!content) return;

  let meta = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attr, name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function setLink(rel, href) {
  if (!href) return;
  let link = document.head.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
}
